import type { RootLocationConfiguration } from '@cognite/apm-config';

import { useIsLocationEnabledContext } from './use-is-location-enabled-for-feature-flag.context';
import {
  isAllLocationsEnabled,
  isCurrentLocationEnabled,
  validateLocationsArray,
} from './utils';

/**
 * Checks if the current selected root location is enabled for the given feature flag.
 * The flag itself also needs to be enabled for this to return true.
 *
 * @param flagKey The Unleash feature flag key.
 * @param selectedRootLocation The currently selected root location configuration.
 * @returns True if the current location is enabled, false otherwise.
 */
export const useIsLocationEnabledForFeatureFlag = (
  flagKey: string,
  selectedRootLocation?: RootLocationConfiguration
): boolean => {
  const { useFlag, useVariant, captureException } =
    useIsLocationEnabledContext();
  const enabledLocations = useVariant(flagKey);
  const { isEnabled: isFlagEnabled } = useFlag(flagKey, {
    fallback: false,
    forceRerender: true,
  });

  if (
    !selectedRootLocation?.externalId ||
    !enabledLocations ||
    !isFlagEnabled
  ) {
    return false;
  }

  try {
    const parsedLocations: string[] = JSON.parse(enabledLocations);

    validateLocationsArray(parsedLocations);

    return (
      isAllLocationsEnabled(parsedLocations) ||
      isCurrentLocationEnabled(parsedLocations, selectedRootLocation.externalId)
    );
  } catch (error) {
    captureException(error);
    return false;
  }
};
